/**
 * Сценарий заставки: все моменты времени в одном месте.
 *
 * Музыка — отрывок «Obsidian Gate» с 23.6 c трека. Два настоящих удара
 * в музыке (найдены по огибающей низких частот) — 28.60 и 35.17 с трека,
 * то есть 5.00 и 11.57 с заставки. На них стоят молния и логотип.
 * Между ними, 8.7–11.5, в музыке пропадает бас — это «затишье»,
 * в нём камера проходит сквозь ворота.
 */

export const CUE = {
  /** Чёрный экран → сцена проявляется. */
  fadeInStart: 0.25,
  fadeInEnd: 2.6,
  /** Надпись «WOGUL GAMES представляет». */
  presentsIn: 0.9,
  presentsOut: 3.9,
  /** Первый удар музыки: молния в шпиль, вороны. */
  hitA: 5.0,
  /** Створки ворот расходятся. */
  gateOpenStart: 6.3,
  gateOpenEnd: 8.9,
  /** Затишье: окна гаснут, в центральном окне — силуэт. */
  lightsOut: 9.35,
  figureIn: 9.95,
  figureOut: 10.95,
  /** Второй удар: логотип врезается в кадр (момент касания). */
  hitB: 11.57,
  /** Затухание в меню. */
  fadeOutStart: 15.2,
  end: 16.4,
} as const;

/** Сколько логотип летит до касания (начинается раньше удара). */
export const LOGO_FLY = 0.16;

// ─── Глубина и камера ─────────────────────────────────────────────────────

/** Масштаб s = D0 / (depth − z): на D0 слой рисуется 1:1. */
export const D0 = 200;

export const DEPTH = {
  hills: 900,
  mansion: 200,
  graves: 172,
  trees: 150,
  gate: 70,
} as const;

function smooth(x: number): number {
  const t = Math.min(1, Math.max(0, x));
  return t * t * (3 - 2 * t);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Ключи пути камеры: [время, z, y]. Между ключами — плавная кривая. */
const CAM_KEYS: Array<[number, number, number]> = [
  [0.0, 0, -92],
  [5.0, 12, -94],
  [8.7, 40, -88],
  [11.3, 95, -112],
  [11.57, 100, -116],
  [16.4, 107, -120],
];

export interface Cam {
  z: number;
  x: number;
  y: number;
}

export function cameraAt(t: number): Cam {
  let z = CAM_KEYS[0][1];
  let y = CAM_KEYS[0][2];
  for (let i = 0; i < CAM_KEYS.length - 1; i++) {
    const [t0, z0, y0] = CAM_KEYS[i];
    const [t1, z1, y1] = CAM_KEYS[i + 1];
    if (t >= t0 && t <= t1) {
      const u = (t - t0) / (t1 - t0);
      // Отрезок к воротам — с разгоном (камера «ныряет»), остальное — плавно.
      const k = i === 2 ? u * u * (2.2 - 1.2 * u) : smooth(u) * 0.5 + u * 0.5;
      z = lerp(z0, z1, k);
      y = lerp(y0, y1, k);
      break;
    }
    if (t > t1) {
      z = z1;
      y = y1;
    }
  }
  // Лёгкое «дыхание» ручной камеры.
  const x = Math.sin(t * 0.37) * 3 + Math.sin(t * 0.91) * 1.2;
  y += Math.sin(t * 0.53) * 1.5;
  return { z, x, y };
}

// ─── Молнии ───────────────────────────────────────────────────────────────

export interface Strike {
  t: number;
  /** Пары [смещение, яркость] — мигание вспышки. */
  pulses: Array<[number, number]>;
  /** Сколько гаснет хвост после последнего импульса. */
  tail: number;
  /** Сила освещения сцены (0..1). */
  power: number;
  /** Где ударить: смещение по x от центра дома (мировые единицы) или null — только облака. */
  boltX: number | null;
  seed: number;
  /** Удар позади дома: в первый миг дом — чёрный силуэт на белом небе. */
  backlit: boolean;
}

export const STRIKES: Strike[] = [
  // далёкие зарницы в облаках во время проявления
  { t: 0.55, pulses: [[0, 0.35], [0.07, 0.05], [0.12, 0.25]], tail: 0.35, power: 0.25, boltX: null, seed: 11, backlit: false },
  { t: 2.9, pulses: [[0, 0.45], [0.05, 0.1], [0.11, 0.4]], tail: 0.4, power: 0.32, boltX: null, seed: 12, backlit: false },
  // предвестник
  { t: 4.86, pulses: [[0, 0.3]], tail: 0.06, power: 0.3, boltX: null, seed: 13, backlit: false },
  // УДАР А — молния в шпиль
  {
    t: CUE.hitA,
    pulses: [[0, 1], [0.05, 0.15], [0.09, 0.95], [0.15, 0.3], [0.21, 0.85], [0.3, 0.45]],
    tail: 0.9,
    power: 1,
    boltX: 26,
    seed: 101,
    backlit: true,
  },
  { t: 7.25, pulses: [[0, 0.55], [0.06, 0.1], [0.1, 0.5]], tail: 0.5, power: 0.5, boltX: -330, seed: 102, backlit: false },
  // в затишье — короткая дальняя
  { t: 10.15, pulses: [[0, 0.25]], tail: 0.25, power: 0.22, boltX: null, seed: 103, backlit: false },
  // УДАР B — двойная молния под логотип
  {
    t: CUE.hitB,
    pulses: [[0, 1], [0.04, 0.3], [0.08, 1], [0.16, 0.45], [0.24, 0.9], [0.36, 0.5]],
    tail: 1.2,
    power: 1,
    boltX: -150,
    seed: 201,
    backlit: true,
  },
  {
    t: CUE.hitB + 0.02,
    pulses: [[0, 0.9], [0.1, 0.8], [0.22, 0.7]],
    tail: 0.9,
    power: 0.9,
    boltX: 170,
    seed: 202,
    backlit: true,
  },
  { t: 13.4, pulses: [[0, 0.6], [0.06, 0.15], [0.12, 0.55]], tail: 0.6, power: 0.55, boltX: 250, seed: 203, backlit: false },
];

/** Яркость одной молнии в момент t (0..1). */
export function strikeLevel(s: Strike, t: number): number {
  const dt = t - s.t;
  if (dt < 0) return 0;
  const p = s.pulses;
  for (let i = p.length - 1; i >= 0; i--) {
    if (dt >= p[i][0]) {
      const v = p[i][1];
      if (i < p.length - 1) {
        // внутри импульса — короткий спад, чтобы мигание было «рваным»
        const seg = p[i + 1][0] - p[i][0];
        const k = (dt - p[i][0]) / seg;
        return v * (1 - 0.35 * k);
      }
      const after = dt - p[i][0];
      return v * Math.exp(-after / Math.max(0.01, s.tail * 0.35));
    }
  }
  return 0;
}

/** Суммарная вспышка (освещение сцены) и признак «контровой» фазы. */
export function flashAt(t: number): { flash: number; backlight: number } {
  let flash = 0;
  let backlight = 0;
  for (const s of STRIKES) {
    const v = strikeLevel(s, t) * s.power;
    flash = Math.max(flash, v);
    if (s.backlit) {
      const dt = t - s.t;
      // первые 50 мс — дом чёрным силуэтом на белом небе
      if (dt >= 0 && dt < 0.05) backlight = Math.max(backlight, 1);
    }
  }
  return { flash: Math.min(1, flash), backlight };
}

// ─── Окна ─────────────────────────────────────────────────────────────────

/** Детерминированный шум для свечи: одинаковый кадр при одинаковом t. */
export function hash1(n: number): number {
  const x = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
}

export function candle(t: number, seed: number): number {
  const a = hash1(Math.floor(t * 9) + seed * 13.7);
  const b = hash1(Math.floor(t * 23) + seed * 7.1);
  return 0.82 + a * 0.12 + b * 0.06;
}

export { smooth, lerp };
